import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20, ordering: 'product_name' };

const state = {
    inventory_items: [],
    inventory_count: 0,
    inventory_loading: false,
    inventory_loading_errors: null,
    inventory_cancel_source: null,
    inventory_filters: DEFAULT_FILTERS,
    selected_date: null,
    inventory_weekly_stats: null,
    inventory_weekly_stats_loading: false,
};

const mutations = {
    updateInventoryItems(state, items) {
        state.inventory_items = items;
    },
    updateInventoryCount(state, count) {
        state.inventory_count = count;
    },
    updateInventoryLoading(state, loading) {
        state.inventory_loading = loading;
    },
    updateInventoryLoadingErrors(state, errors) {
        state.inventory_loading_errors = errors;
    },
    updateInventoryCancelSource(state, source) {
        state.inventory_cancel_source = source;
    },
    updateInventoryFilters(state, filters) {
        state.inventory_filters = filters;
        saveFiltersToStorage(getStorageKey('inventory', 'default'), state.inventory_filters);
    },
    updateSelectedDate(state, date) {
        state.selected_date = date;
    },
    updateInventoryWeeklyStats(state, stats) {
        state.inventory_weekly_stats = stats;
    },
    updateInventoryWeeklyStatsLoading(state, loading) {
        state.inventory_weekly_stats_loading = loading;
    },
};

const actions = {
    async fetchInventory({ commit, state }, params = {}) {
        commit('updateInventoryLoading', true);
        commit('updateInventoryLoadingErrors', null);

        if (state.inventory_cancel_source) {
            state.inventory_cancel_source.cancel("canceled");
        }

        const cancelSource = axios.CancelToken.source();
        commit('updateInventoryCancelSource', cancelSource);

        try {
            const filters = { ...state.inventory_filters, ...params };
            if (state.selected_date) {
                filters.movement_date_max = state.selected_date;
            }
            // Remove pagination params as inventory endpoint doesn't support them
            delete filters.offset;
            delete filters.limit;

            const response = await axios.get('/api/inventory-export/', {
                params: filters,
                cancelToken: cancelSource.token
            });

            commit('updateInventoryLoading', false);
            commit('updateInventoryItems', response.data.results || response.data);
            commit('updateInventoryCount', response.data.count || (response.data.results ? response.data.results.length : response.data.length));
            commit('updateInventoryCancelSource', null);
            return response;
        } catch (xhr_error) {
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateInventoryLoadingErrors', error.details);
            commit('updateInventoryLoading', false);
            throw error;
        }
    },

    downloadInventory({ commit, state }, params = {}) {
        const filters = { ...state.inventory_filters, ...params };
        if (state.selected_date) {
            filters.movement_date_max = state.selected_date;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        filters.offset = null;

        const dateStr = state.selected_date ? state.selected_date.replace(/-/g, '') : new Date().toISOString().split('T')[0].replace(/-/g, '');
        $utils.downloadFile({
            url: "/api/inventory-export/",
            params: filters,
        }, $utils.getFilename('inventory', { date: dateStr }));
    },

    async exportInventoryAsCSV({ commit, state }, params = {}) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.inventory_filters);
            filters.offset = null;
        } else {
            // When exporting everything, still include customer if set (required for operators)
            if (state.inventory_filters.customer != null) {
                filters.customer = state.inventory_filters.customer;
            }
        }
        if (state.selected_date) {
            filters.movement_date_max = state.selected_date;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;

        const dateStr = state.selected_date ? state.selected_date.replace(/-/g, '') : new Date().toISOString().split('T')[0].replace(/-/g, '');
        $utils.downloadFile({
            url: "/api/inventory-export/",
            params: filters,
        }, $utils.getFilename('inventory', { date: dateStr }));
    },

    async fetchInventoryWeeklyStats({ commit, state }) {
        commit('updateInventoryWeeklyStatsLoading', true);
        try {
            const params = {};
            if (state.selected_date) {
                params.movement_date_max = state.selected_date;
            }
            const filters = { ...state.inventory_filters };
            // Remove params not supported or not meaningful for the historical stock chart
            delete filters.offset;
            delete filters.limit;
            delete filters.ordering;
            delete filters.exclude_empty_stock;
            delete filters.missing_purchase_price;
            delete filters.search;
            Object.assign(params, filters);
            
            const response = await axios.get('/api/movements/inventory/weekly-stats/', {
                params,
            });
            commit('updateInventoryWeeklyStats', response.data);
            commit('updateInventoryWeeklyStatsLoading', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateInventoryWeeklyStatsLoading', false);
            throw error;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
};
